import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import { activeAppFrame } from "./activeAppFrame";

/** How long the parent keeps re-offering the channel when the app document is
 *  still loading. The child announces itself, but the first `connect` can land
 *  before its script runs; without a bounded retry the handshake would only
 *  complete on the iframe `load` event — which third-party ads can delay for
 *  seconds. */
const CONNECT_RETRY_MS = 250;
const CONNECT_RETRIES = 20;

export function allowedAppNavigation(
  value: string,
  current: string,
): URL | null {
  try {
    const url = new URL(value, current);
    if (
      !/^https?:$/.test(url.protocol) ||
      url.origin !== new URL(current).origin ||
      url.username ||
      url.password ||
      /^\/__ato(?:\/|$)/.test(decodeURIComponent(url.pathname))
    )
      return null;
    return url;
  } catch {
    return null;
  }
}

/** A message addressed to the app origin is rejected (and logged) while the
 *  frame still shows its initial about:blank document, which inherits this
 *  page's origin. The app's document is cross-origin, so reading its URL
 *  throws once the navigation has committed — before the load event, which
 *  third-party requests can delay. */
export function frameHasAppDocument(frame: HTMLIFrameElement | null): boolean {
  try {
    const href = frame?.contentWindow?.location.href;
    return Boolean(href) && href !== "about:blank";
  } catch {
    return true;
  }
}

export function useAppNavigationBridge(root: RefObject<HTMLElement>) {
  const [state, setState] = useState<{
    url: string;
    canBack: boolean;
    canForward: boolean;
  } | null>(null);
  const connection = useRef<{
    frame: HTMLIFrameElement;
    origin: string;
    channel: string;
  } | null>(null);
  useEffect(() => {
    let frame: HTMLIFrameElement | null = null;
    let source = "";
    let loadedDocument = false;
    let retry: ReturnType<typeof setInterval> | null = null;
    const stopRetry = () => {
      if (retry !== null) {
        clearInterval(retry);
        retry = null;
      }
    };
    const postConnect = () => {
      const active = connection.current;
      if (!active || !frame?.contentWindow || !(loadedDocument || frameHasAppDocument(frame))) return;
      try {
        active.frame.contentWindow?.postMessage(
          { type: "ato.app-navigation.v1", action: "connect", channel: active.channel },
          active.origin,
        );
      } catch {
        /* the frame navigated or was removed between the checks */
      }
    };
    const startRetry = () => {
      stopRetry();
      let attempts = 0;
      retry = setInterval(() => {
        attempts += 1;
        if (!connection.current || attempts >= CONNECT_RETRIES) {
          stopRetry();
          return;
        }
        postConnect();
      }, CONNECT_RETRY_MS);
    };
    const connect = () => {
      stopRetry();
      setState(null);
      connection.current = null;
      if (!frame?.contentWindow) return;
      try {
        const url = new URL(frame.src);
        if (!/^https?:$/.test(url.protocol)) return;
        const channel = crypto.randomUUID();
        connection.current = { frame, origin: url.origin, channel };
        postConnect();
        startRetry();
      } catch {
        /* srcdoc, opaque and non-web surfaces are unsupported. */
      }
    };
    const onLoad = () => {
      loadedDocument = true;
      connect();
    };
    const observe = () => {
      const next = activeAppFrame(root.current);
      if (next === frame && next?.src === source) return;
      frame?.removeEventListener("load", onLoad);
      // A new frame or a new src starts again on about:blank until its own
      // document commits; never carry the previous readiness across.
      loadedDocument = false;
      frame = next;
      source = frame?.src ?? "";
      frame?.addEventListener("load", onLoad);
      connect();
    };
    const receive = (event: MessageEvent) => {
      const active = connection.current,
        data = event.data;
      // A channel message is only ever accepted from the frame that holds it.
      if (
        !active ||
        event.source !== active.frame.contentWindow ||
        event.origin !== active.origin
      )
        return;
      // The child can announce itself before the parent's first `connect`
      // arrived; answer it instead of waiting for `load`.
      if (data?.type === "ato.app-navigation.v1.hello") {
        // The child can only send this from its own document.
        loadedDocument = true;
        postConnect();
        return;
      }
      if (
        data?.type !== "ato.app-navigation.v1.state" ||
        data.channel !== active.channel ||
        typeof data.url !== "string" ||
        typeof data.can_back !== "boolean" ||
        typeof data.can_forward !== "boolean"
      )
        return;
      const url = allowedAppNavigation(data.url, active.frame.src);
      if (!url) return;
      stopRetry();
      [
        "app_view_token",
        "token",
        "access_token",
        "authorization",
        "code",
      ].forEach((key) => url.searchParams.delete(key));
      setState({
        url: url.href,
        canBack: data.can_back,
        canForward: data.can_forward,
      });
    };
    window.addEventListener("message", receive);
    const observer = new MutationObserver(observe);
    if (root.current)
      observer.observe(root.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["src", "hidden"],
      });
    observe();
    return () => {
      stopRetry();
      frame?.removeEventListener("load", onLoad);
      observer.disconnect();
      window.removeEventListener("message", receive);
      connection.current = null;
    };
  }, [root]);
  const command = useCallback(
    (action: "back" | "forward" | "reload" | "navigate", url?: string) => {
      const active = connection.current;
      if (!active || !state) return false;
      if (
        action === "navigate" &&
        (!url || !allowedAppNavigation(url, state.url))
      )
        return false;
      active.frame.contentWindow?.postMessage(
        { type: "ato.app-navigation.v1", action, url, channel: active.channel },
        active.origin,
      );
      return true;
    },
    [state],
  );
  return { state, command };
}
